import * as React from 'react';
import {
  CheckOutlined,
  CloseCircleFilled,
  ExclamationCircleOutlined,
  FileFilled,
} from '@ant-design/icons';
import { Button, Progress, theme, Typography } from 'antd';
import clsx from 'clsx';
import { FileWithBackendId, Status } from '@hooks/useUpload/useUpload';
import { ALT_FALLBACK, ERROR_UPLOADING } from '@constants/chat';
import styles from './Thumbnail.module.scss';

const { Paragraph } = Typography;
const { useToken } = theme;

type Props = {
  onRemoveFiles: (_: Pick<FileWithBackendId, 'id' | 'idx'>) => void;
  data: FileWithBackendId;
};

export const Thumbnail: React.FC<Props> = ({
  onRemoveFiles,
  data: { file, id, progress, logo, size, idx, ...fileData },
}) => {
  const { token } = useToken();

  const hasError = fileData.state === Status.Error;
  const fileLogo = logo ?? { Img: FileFilled, col: '' };
  const uploadComplete = progress === 100;

  React.useEffect(() => {
    return () => {
      if (fileData.data.isImage) {
        URL.revokeObjectURL?.(fileData.data.url);
      }
    };
  }, [fileData.data]);

  return (
    <article
      className={clsx(styles.thumbnail, {
        [styles.thumbnail_preview]: fileData.canPreview,
      })}
    >
      <div
        className={clsx(styles.thumbnailStatus, {
          [styles.thumbnailStatus_none]: uploadComplete,
        })}
      >
        <Progress
          type="circle"
          percent={progress}
          size={40}
          strokeColor={uploadComplete ? token.colorSuccess : token.colorWhite}
          format={() =>
            uploadComplete ? (
              <CheckOutlined />
            ) : (
              <span className={styles.thumbnailStatus_score}>{progress}%</span>
            )
          }
          style={{ color: token.colorWhite }}
        />
      </div>
      <Button
        className={styles.thumbnailRemove}
        icon={<CloseCircleFilled />}
        onClick={(e) => {
          e.stopPropagation();
          onRemoveFiles({ id, idx });
        }}
      />
      {fileData.data.isImage && (
        <div className={styles.thumbnailImgWrapper}>
          <div
            className={clsx(styles.thumbnailImgStatus, {
              [styles.thumbnailStatus]: hasError,
              [styles.thumbnailStatus_none]: !hasError,
            })}
          >
            <ExclamationCircleOutlined className={styles.thumbnailErrorIcon} />
            <Paragraph className={styles.thumbnailImgText}>
              {ERROR_UPLOADING}&nbsp;{file.name}
            </Paragraph>
          </div>
          <img
            alt={file?.name ?? ALT_FALLBACK}
            src={fileData.data.url}
            className={styles.thumbnailImg}
          />
        </div>
      )}
      {!fileData.data.isImage && (
        <div className={clsx(styles.thumbnailFile, { [styles.thumbnail_error]: hasError })}>
          {hasError ? (
            <ExclamationCircleOutlined
              className={(styles.thumbnailErrorIcon, styles.thumbnailMainIcon)}
            />
          ) : (
            <fileLogo.Img
              data-testid={`thumblogo-${id}`}
              className={styles.thumbnailMainIcon}
              style={{ color: fileLogo.col }}
            />
          )}
          <div className={styles.thumbnailInfo}>
            <Paragraph className={styles.thumbnailTitle}>{file.originFileObj?.name}</Paragraph>
            <Paragraph
              className={clsx(styles.thumbnailSize, {
                [styles.thumbnailSize_error]: hasError,
              })}
            >
              {hasError ? `${ERROR_UPLOADING} ${file.name}` : size}
            </Paragraph>
          </div>
        </div>
      )}
    </article>
  );
};
